import React, {useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import Styled from 'styled-components';
import Util from '../../../../../util/util';
import {Column, TaskRowDropdownStyle} from '../Elements.styled';
import DropdownV2 from '../../../../dropdowns/dropdown';
import {CUSTOM_FIELD_TYPE, profileLocales} from '../../../../../../../constants';

import UpdateCustomFieldValueMutation from '../../../../../../../mutations/update_custom_field_value_mutation';
import {GreyText} from '../../../../../../project-tab/projects/project-settings/ProjectSettings_styled';
import {FormattedMessage, useIntl} from 'react-intl';

const ColumnWrapper = styled.div`
	display: flex;
`;

const CustomFieldInput = Styled.input`
    background-color: ${props => props.backgroundColor};
    color: ${({backgroundColor}) => Util.getTextColorV2(backgroundColor)};
    border-radius: 4px;
    border: ${props => (props.showBox ? '1px solid #e6e6e6' : '0px')};
    padding: ${props => (props.showBox ? '6px 15px 6px 10px' : '0px')};
    text-overflow: ellipsis;
    height: 24px;
    width: 100%;
    &:hover {
        background-color: ${props => (props.showBox ? props.backgroundColor : 'rgba(0, 0, 0, 0.1)')};
    }
    &:focus {
   		background-color: ${props => (props.showBox ? props.backgroundColor : 'rgba(0, 0, 0, 0.2)')};
        outline: none;
    }
`;

const isValidInteger = val => {
	if (val === null || val === undefined || val === '') return true;
	return /^-?\d+$/.test(val);
};

const filterIntegerInput = val => {
	const stripped = val.replace(/[^0-9-]/g, '');
	if (stripped === '-') return '-';
	const negative = stripped.startsWith('-');
	const digits = stripped.replace(/-/g, '');
	return (negative ? '-' : '') + digits;
};

// Accepts both '.' and ',' so paste or the non-locale separator still validates.
const isValidDecimal = val => {
	if (val === null || val === undefined || val === '') return true;
	return /^-?\d+(\.\d+)?$/.test(String(val).replace(',', '.'));
};

const filterDecimalInput = (val, decSep) => {
	const allowPattern = decSep === ',' ? /[^0-9,-]/g : /[^0-9.-]/g;
	const dupPattern = decSep === ',' ? /,/g : /\./g;
	let result = val.replace(allowPattern, '');
	const isNegative = result.startsWith('-');
	result = result.replace(/-/g, '');
	if (isNegative) result = '-' + result;
	const sepIdx = result.indexOf(decSep);
	if (sepIdx !== -1) {
		result = result.slice(0, sepIdx + 1) + result.slice(sepIdx + 1).replace(dupPattern, '');
	}
	return result;
};

export const CustomFieldColumn = ({
	readOnly,
	customFieldKey,
	customField,
	entityType,
	entityId,
	width,
	cy = '',
	backgroundColor,
	showBox,
	language,
}) => {
	const {formatMessage} = useIntl();
	const {value = '', type, options = [], __typename} = customField || {};

	const decSep = profileLocales[language]?.usesCommaDecimal ? ',' : '.';
	// Convert stored '.' to locale separator.
	const toDisplay = val => {
		if (val === null || val === undefined || val === '') return val;
		return decSep === ',' ? String(val).replace('.', ',') : String(val);
	};
	// For decimal fields only: also ensure one decimal place (4 → 4.0 / 4,0).
	const toDecimalDisplay = val => {
		const withSep = toDisplay(val);
		if (withSep === null || withSep === undefined || withSep === '') return withSep;
		return withSep.includes(decSep) ? withSep : withSep + decSep + '0';
	};
	const toDisplayForType = type === CUSTOM_FIELD_TYPE.DECIMAL ? toDecimalDisplay : toDisplay;
	const toStored = val => (val != null ? String(val).replace(',', '.') : val);

	const [currentValue, setCurrentValue] = useState(toDisplayForType(value));

	useEffect(() => {
		setCurrentValue(toDisplayForType(value));
	}, [value, decSep]);

	useEffect(() => {
		setCurrentValue(currentValue);
	}, [currentValue]);
	const inputRef = useRef();

	const handleValueChange = e => {
		if (type === CUSTOM_FIELD_TYPE.INTEGER) {
			const rawValue = e.target.value;
			const filtered = filterIntegerInput(rawValue);
			if (filtered === '' && rawValue !== '') {
				e.target.value = currentValue ?? '';
				return;
			}
			setCurrentValue(filtered);
		} else if (type === CUSTOM_FIELD_TYPE.DECIMAL) {
			const filtered = filterDecimalInput(e.target.value, decSep);
			setCurrentValue(filtered);
		} else {
			setCurrentValue(e.target.value);
		}
	};

	const handleKeyPress = e => {
		if (e.key === 'Enter') {
			inputRef?.current?.blur();
		}
	};

	const updateCustomFieldMutation = newValue => {
		Util.CommitMutation(UpdateCustomFieldValueMutation, {
			__typename,
			entityId,
			entityType,
			key: customFieldKey,
			value: newValue,
		});
	};

	const handleSave = () => {
		// Compare normalised stored representations to avoid false dirty on locale conversion.
		if (toStored(String(value ?? '')) !== toStored(String(currentValue ?? ''))) {
			if (type === CUSTOM_FIELD_TYPE.INTEGER && !isValidInteger(currentValue)) {
				setCurrentValue(toDisplay(value) ?? '');
				return;
			}
			if (type === CUSTOM_FIELD_TYPE.DECIMAL) {
				if (!isValidDecimal(currentValue)) {
					setCurrentValue(toDecimalDisplay(value) ?? '');
					return;
				}
				if (currentValue !== '' && currentValue !== null) {
					const stored = toStored(String(currentValue));
					const normalized = stored.includes('.') ? stored : stored + '.0';
					setCurrentValue(toDecimalDisplay(normalized));
					updateCustomFieldMutation(normalized);
					return;
				}
			}
			updateCustomFieldMutation(toStored(String(currentValue ?? '')));
		}
	};

	const onChangeDropdownInput = newValue => {
		setCurrentValue(newValue);
		updateCustomFieldMutation(newValue);
	};

	return (
		<Column width={width}>
			{readOnly ? (
				<span>{currentValue}</span>
			) : (
				<ColumnWrapper>
					{/* TODO: Use common CustomFieldValueInput component here instead. */}
					{type === CUSTOM_FIELD_TYPE.DROPDOWN ? (
						<TaskRowDropdownStyle>
							<DropdownV2
								placeholder={formatMessage({id: 'common.select_value'})}
								clearable
								clearText={
									<GreyText>
										<FormattedMessage id="common.none" />
									</GreyText>
								}
								optionClickEvent={true}
								onChange={option => onChangeDropdownInput(option && option.value)}
								options={options.map(option => ({
									label: option.displayName,
									value: atob(option.id).split(':')[1],
									searchString: option.displayName,
								}))}
								value={currentValue}
								hideLabel
								customWidth={158}
								listDataCy={'custom-field-list'}
								buttonCy={'custom-field-arrow'}
								inputCy={'custom-field-input'}
								customHeight={30}
							/>
						</TaskRowDropdownStyle>
					) : (
						<CustomFieldInput
							onClick={e => e.stopPropagation()}
							data-cy={'custom-field-value-input' + cy}
							type="text"
							ref={inputRef}
							value={currentValue}
							onChange={handleValueChange}
							onBlur={handleSave}
							spellCheck={false}
							maxLength={191}
							onKeyPress={handleKeyPress}
							backgroundColor={backgroundColor}
							showBox={showBox}
						/>
					)}
				</ColumnWrapper>
			)}
		</Column>
	);
};
